// src/pages/Login.jsx
import React, { useState, useEffect, useRef } from "react";
import { supabase } from "../shared/supabase";
import { useNavigate } from "react-router-dom";

export default function Login() {
    const [formData, setFormData] = useState({ email: "", password: "" });
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [headingVisible, setHeadingVisible] = useState(false);
    const canvasRef = useRef(null);
    const [attempts, setAttempts] = useState(0);
    const [isLocked, setIsLocked] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        setHeadingVisible(true);
        createParticleBackground();
    }, []);

    const createParticleBackground = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles = [];
        const colors = [
            '#ff006e', '#fb5607', '#ffbe0b', '#3a86ff', '#8338ec',
            '#ff006e', '#ff5e00', '#ffbd00', '#00d4ff', '#9d4edd'
        ];

        class Particle {
            constructor() {
                this.x = Math.random() * canvas.width;
                this.y = Math.random() * canvas.height;
                this.size = Math.random() * 20 + 5;
                this.speedX = Math.random() * 3 - 1.5;
                this.speedY = Math.random() * 3 - 1.5;
                this.color = colors[Math.floor(Math.random() * colors.length)];
                this.rotation = Math.random() * 360;
                this.rotationSpeed = Math.random() * 2 - 1;
                this.pulseSpeed = Math.random() * 0.05 + 0.01;
                this.pulseSize = this.size;
            }

            update() {
                this.x += this.speedX;
                this.y += this.speedY;
                this.rotation += this.rotationSpeed;

                // Pulse animation
                this.pulseSize = this.size + Math.sin(Date.now() * this.pulseSpeed) * 5;

                // Bounce off walls
                if (this.x < 0 || this.x > canvas.width) this.speedX *= -1;
                if (this.y < 0 || this.y > canvas.height) this.speedY *= -1;
            }

            draw() {
                ctx.save();
                ctx.translate(this.x, this.y);
                ctx.rotate(this.rotation * Math.PI / 180);

                // Create gradient for each particle
                const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, this.pulseSize);
                gradient.addColorStop(0, this.color);
                gradient.addColorStop(1, 'transparent');

                ctx.fillStyle = gradient;
                ctx.globalCompositeOperation = 'lighter';

                // Draw various shapes
                if (Math.random() > 0.5) {
                    // Star shape
                    this.drawStar(0, 0, 5, this.pulseSize, this.pulseSize / 2);
                } else {
                    // Circle
                    ctx.beginPath();
                    ctx.arc(0, 0, this.pulseSize, 0, Math.PI * 2);
                    ctx.fill();
                }

                ctx.restore();
            }

            drawStar(cx, cy, spikes, outerRadius, innerRadius) {
                let rot = Math.PI / 2 * 3;
                let x = cx;
                let y = cy;
                const step = Math.PI / spikes;

                ctx.beginPath();
                ctx.moveTo(cx, cy - outerRadius);

                for (let i = 0; i < spikes; i++) {
                    x = cx + Math.cos(rot) * outerRadius;
                    y = cy + Math.sin(rot) * outerRadius;
                    ctx.lineTo(x, y);
                    rot += step;

                    x = cx + Math.cos(rot) * innerRadius;
                    y = cy + Math.sin(rot) * innerRadius;
                    ctx.lineTo(x, y);
                    rot += step;
                }

                ctx.lineTo(cx, cy - outerRadius);
                ctx.closePath();
                ctx.fill();
            }
        }

        // Create particles
        for (let i = 0; i < 50; i++) {
            particles.push(new Particle());
        }

        function animate() {
            ctx.fillStyle = 'rgba(10, 10, 30, 0.1)';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            particles.forEach(particle => {
                particle.update();
                particle.draw();
            });

            requestAnimationFrame(animate);
        }

        animate();

        // Handle resize
        const handleResize = () => {
            canvas.width = window.innerWidth;
            canvas.height = window.innerHeight;
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData((prev) => ({ ...prev, [id]: value }));
        setError("");
        setSuccess(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isLocked) return; // prevent submission if locked

        const emailRegex = /^[\w.-]+@([\w-]+\.)+[\w-]{2,4}$/;

        if (!formData.email || !formData.password) {
            setError("Please fill in all fields.");
            return;
        }
        if (!emailRegex.test(formData.email)) {
            setError("Please enter a valid email address.");
            return;
        }

        // Supabase login attempt
        const { error } = await supabase.auth.signInWithPassword({
            email: formData.email,
            password: formData.password,
        });

        if (error) {
            setAttempts(prev => prev + 1);
            setError(`Login failed! Attempts left: ${5 - (attempts + 1)}`);

            if (attempts + 1 >= 5) {
                setIsLocked(true);
                setError("Too many failed attempts! Please wait 5 minutes.");

                // Optional: auto-unlock after 5 minutes
                setTimeout(() => {
                    setIsLocked(false);
                    setAttempts(0);
                    setError("");
                }, 5 * 60 * 1000);
            }

            return;
        }

        // Successful login
        setSuccess(true);
        setError("");
        setFormData({ email: "", password: "" });
        setAttempts(0);

        // Redirect to dashboard
        navigate("/dashboard");
    };


    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-purple-900 to-blue-900 overflow-hidden relative">
            {/* Animated Canvas Background */}
            <canvas
                ref={canvasRef}
                className="absolute inset-0 w-full h-full"
            />

            {/* Floating Shapes */}
            <div className="absolute top-1/4 left-1/4 w-20 h-20 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full opacity-60 animate-float-1 shadow-2xl shadow-cyan-400/50"></div>
            <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full opacity-70 animate-float-2 shadow-2xl shadow-pink-400/50"></div>
            <div className="absolute bottom-1/4 left-1/3 w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full opacity-50 animate-float-3 shadow-2xl shadow-green-400/50"></div>
            <div className="absolute top-2/3 right-1/3 w-12 h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-80 animate-float-4 shadow-2xl shadow-yellow-400/50"></div>

            {/* Main Container */}
            <div className="relative bg-black/40 backdrop-blur-2xl rounded-3xl shadow-2xl w-full max-w-md p-10 flex flex-col items-center space-y-8 border border-white/20 hover:border-white/40 transition-all duration-1000 hover:shadow-cyan-500/20 hover:shadow-2xl">

                {/* Animated Heading */}
                <div className="text-center space-y-4">
                    <h1
                        className={`text-6xl font-black text-center bg-gradient-to-r from-cyan-400 via-purple-500 to-pink-500 bg-clip-text text-transparent tracking-wider transform transition-all duration-2000 ${headingVisible ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-50 rotate-45"
                            } animate-pulse`}
                        style={{
                            backgroundSize: '200% 200%',
                            animation: 'gradientShift 3s ease infinite, textGlow 2s ease-in-out infinite alternate'
                        }}
                    >
                        WELCOME BACK
                    </h1>
                    <p className="text-white/80 font-bold text-lg text-center animate-bounce">
                        🔐 Login to Your Universe 🔐
                    </p>
                </div>

                {/* Error/Success Messages */}
                {error && (
                    <div className="bg-gradient-to-r from-red-500 to-pink-600 text-white px-6 py-4 rounded-2xl mb-4 shadow-2xl animate-shake border border-white/20 backdrop-blur-lg">
                        <div className="flex items-center space-x-2">
                            <span className="text-xl">⚠️</span>
                            <span className="font-bold">{error}</span>
                        </div>
                    </div>
                )}
                {success && (
                    <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-2xl mb-4 shadow-2xl animate-tada border border-white/20 backdrop-blur-lg">
                        <div className="flex items-center space-x-2">
                            <span className="text-xl">🎉</span>
                            <span className="font-bold">Login Successful! Redirecting...</span>
                        </div>
                    </div>
                )}

                {/* Form */}
                <form onSubmit={handleSubmit} className="w-full space-y-8">
                    {/* Email Field */}
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                        <input
                            type="email"
                            id="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="🚀 Enter your cosmic email"
                            disabled={isLocked}
                            className="relative w-full px-6 py-5 rounded-2xl border-2 border-transparent bg-black/60 text-white placeholder-white/60 focus:placeholder-transparent focus:outline-none focus:ring-4 focus:ring-cyan-500/50 shadow-2xl transition-all duration-500 transform group-hover:scale-105 font-bold text-lg backdrop-blur-lg"
                        />
                        <span className="absolute right-6 top-1/2 transform -translate-y-1/2 text-2xl animate-bounce delay-75">
                            ✨
                        </span>
                    </div>

                    {/* Password Field */}
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-pink-500 to-rose-600 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200 animate-pulse"></div>
                        <input
                            type="password"
                            id="password"
                            value={formData.password}
                            onChange={handleChange}
                            placeholder="🔑 Enter your secret password"
                            disabled={isLocked}
                            className="relative w-full px-6 py-5 rounded-2xl border-2 border-transparent bg-black/60 text-white placeholder-white/60 focus:placeholder-transparent focus:outline-none focus:ring-4 focus:ring-pink-500/50 shadow-2xl transition-all duration-500 transform group-hover:scale-105 font-bold text-lg backdrop-blur-lg"
                        />
                        <span className="absolute right-6 top-1/2 transform -translate-y-1/2 text-2xl animate-spin-slow">
                            🌟
                        </span>
                    </div>

                    {/* Ultra Animated Submit Button */}
                    <button
                        type="submit"
                        disabled={isLocked}
                        className="relative w-full py-6 text-white font-black text-2xl rounded-3xl overflow-hidden transform transition-all duration-700 hover:scale-105 active:scale-95 shadow-2xl group"
                        style={{
                            background: 'linear-gradient(45deg, #ff0080, #ff8c00, #40e0d0, #ff0080)',
                            backgroundSize: '400% 400%',
                            animation: 'gradientShift 3s ease infinite'
                        }}
                    >
                        {/* Animated background */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000"></div>

                        {/* Button content */}
                        <span className="relative flex items-center justify-center space-x-3">
                            <span className="animate-pulse">🚀</span>
                            <span className="text-shadow">BLAST OFF!</span>
                            <span className="animate-bounce">⭐</span>
                        </span>

                        {/* Additional glow effect */}
                        <div className="absolute inset-0 rounded-3xl border-2 border-white/50 shadow-inner shadow-white/25"></div>
                    </button>
                </form>
            </div>

            {/* Custom Styles */}
            <style jsx>{`
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes textGlow {
          0% { text-shadow: 0 0 20px rgba(255, 255, 255, 0.5); }
          100% { text-shadow: 0 0 30px rgba(255, 255, 255, 0.8), 0 0 40px rgba(255, 255, 255, 0.6); }
        }
        
        @keyframes float-1 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }
        
        @keyframes float-2 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-30px) rotate(-180deg); }
        }
        
        @keyframes float-3 {
          0%, 100% { transform: translateY(0px) scale(1); }
          50% { transform: translateY(-25px) scale(1.2); }
        }
        
        @keyframes float-4 {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(90deg); }
        }
        
        .animate-float-1 { animation: float-1 6s ease-in-out infinite; }
        .animate-float-2 { animation: float-2 8s ease-in-out infinite; }
        .animate-float-3 { animation: float-3 7s ease-in-out infinite; }
        .animate-float-4 { animation: float-4 5s ease-in-out infinite; }
        
        .text-shadow {
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }
      `}</style>
        </div>
    );
}